import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    ClockIcon,
    ArrowPathIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    SparklesIcon,
    CheckCircleIcon,
    ChevronLeftIcon,
    InformationCircleIcon,
    TrashIcon,
    PaperAirplaneIcon,
    LinkIcon,
    BeakerIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import { Slider } from '../components/ui/Slider';
import { userStoryService, UserStory } from '../services/userStoryService';
import { coreService } from '../services/coreService';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import toast from 'react-hot-toast';

export const UserStoryDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useSelector((state: RootState) => state.auth);
    const isPro = user?.subscribed === 'S' || user?.plan === 'premier' || user?.plan === 'enterprise';

    const [userStory, setUserStory] = useState<UserStory | null>(null);
    const [loading, setLoading] = useState(true);
    const [generatingTasks, setGeneratingTasks] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Task Edit State
    const [selectedTask, setSelectedTask] = useState<any | null>(null);
    const [editTaskTitle, setEditTaskTitle] = useState('');
    const [editTaskDescription, setEditTaskDescription] = useState('');
    const [isSavingTask, setIsSavingTask] = useState(false);
    const [isDeletingTask, setIsDeletingTask] = useState(false);
    const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
    const [isEnhancing, setIsEnhancing] = useState(false);

    // Chat State
    const [chatInput, setChatInput] = useState('');
    const [isChatting, setIsChatting] = useState(false);
    const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'ai', content: string }[]>([]);

    const [showTaskGenerationModal, setShowTaskGenerationModal] = useState(false);
    const [extraContext, setExtraContext] = useState('');
    const [taskCount, setTaskCount] = useState(5);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [progressSteps, setProgressSteps] = useState<{ label: string; status: 'completed' | 'loading' | 'pending' }[]>([]);

    useEffect(() => {
        if (id) {
            loadUserStory();
        }
    }, [id]);

    const loadUserStory = async () => {
        try {
            setLoading(true);
            const response = await userStoryService.getUserStory(id!);
            setUserStory(response.data);
        } catch (err: any) {
            setError(err.message || 'Failed to load user story');
            toast.error('Failed to load user story');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenGenerateModal = () => {
        setShowTaskGenerationModal(true);
        setExtraContext('');
    };

    const handleGenerateTasks = async () => {
        if (!userStory) return;

        setShowTaskGenerationModal(false);
        setShowProgressModal(true);
        setGeneratingTasks(true);
        setError(null);

        setProgressSteps([
            { label: 'Skipped research phase - no tools were available', status: 'completed' },
            { label: `Generating Tasks for US: ${userStory.title}`, status: 'loading' }
        ]);

        try {
            const response = await userStoryService.generateTasks(userStory.id, {
                extraContext: extraContext || undefined,
                regenerate: userStory.tasks && userStory.tasks.length > 0,
                count: taskCount
            });

            if (response.success) {
                setProgressSteps(prev => prev.map(s => s.status === 'loading' ? { ...s, status: 'completed' } : s));
                await new Promise(resolve => setTimeout(resolve, 800));
                await loadUserStory();
                setShowProgressModal(false);
                toast.success('Tasks generated successfully');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to generate tasks');
            setShowProgressModal(false);
            toast.error('Failed to generate tasks');
        } finally {
            setGeneratingTasks(false);
        }
    };

    const handleTaskClick = (task: any) => {
        setSelectedTask(task);
        setEditTaskTitle(task.title);
        // Convert \n to <br> for proper display in ReactQuill and make "Acceptance Criteria:" bold
        const formattedDescription = task.description
            ? task.description
                .replace(/\\n/g, '<br>')
                .replace(/\n/g, '<br>')
                .replace(/Acceptance Criteria:/g, '<strong>Acceptance Criteria:</strong>')
            : '';
        setEditTaskDescription(formattedDescription);
        setChatHistory([{ role: 'ai', content: 'Generating suggestions for you...' }]);
    };

    const handleAddNewTask = () => {
        const newTask = { id: 'new', title: 'New Task' };
        setSelectedTask(newTask);
        setEditTaskTitle('');
        setEditTaskDescription('');
        setChatHistory([{ role: 'ai', content: 'Describe the new task you want to create.' }]);
    }

    const handleSaveTask = async () => {
        if (!userStory || !selectedTask) return;
        if (!editTaskTitle.trim()) {
            toast.error('Task name is required');
            return;
        }

        setIsSavingTask(true);
        try {
            // Convert <br> back to \n for storage
            const descriptionForSave = editTaskDescription
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/&nbsp;/g, ' ');

            const taskData = {
                userStoryId: userStory.id,
                projectId: userStory.projectId,
                title: editTaskTitle,
                description: descriptionForSave,
                type: selectedTask.type || 'development',
                status: selectedTask.status || 'todo'
            };

            if (selectedTask.id === 'new') {
                await userStoryService.createTask(taskData);
                toast.success('Task created successfully');
            } else {
                await userStoryService.updateTask(selectedTask.id, taskData);
                toast.success('Task updated successfully');
            }
            await loadUserStory();
            setSelectedTask(null); // Return to list view
        } catch (err: any) {
            console.error('Save task error:', err);
            toast.error('Failed to save task');
        } finally {
            setIsSavingTask(false);
        }
    };

    const handleDeleteTask = async () => {
        if (!selectedTask || selectedTask.id === 'new') return;
        setIsDeletingTask(true);
        try {
            await userStoryService.deleteTask(selectedTask.id);
            toast.success('Task deleted successfully');
            setShowDeleteTaskModal(false);
            setSelectedTask(null);
            await loadUserStory();
        } catch (err) {
            console.error('Delete task error:', err);
            toast.error('Failed to delete task');
        } finally {
            setIsDeletingTask(false);
        }
    };

    const handleEnhanceTask = async () => {
        if (!editTaskDescription) return;
        setIsEnhancing(true);
        try {
            const result = await coreService.enhance({
                content: editTaskDescription,
                instruction: "Enhance this task description to be more technical, clear, and actionable. Include specific implementation details if missing.",
                context: {
                    userStoryTitle: userStory?.title,
                    userStoryDescription: userStory?.description
                }
            });

            if (result.success && result.data.editedContent) {
                setEditTaskDescription(result.data.editedContent);
                toast.success('Description enhanced by AI');
            }
        } catch (err) {
            toast.error('AI Enhancement failed');
        } finally {
            setIsEnhancing(false);
        }
    };

    const handleChat = async () => {
        if (!chatInput.trim()) return;
        const userMsg = chatInput;
        setChatInput('');
        setChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
        setIsChatting(true);

        try {
            const response = await coreService.chat({
                message: userMsg,
                context: {
                    userStory: userStory,
                    currentTask: {
                        title: editTaskTitle,
                        description: editTaskDescription
                    }
                }
            });
            setChatHistory(prev => [...prev, { role: 'ai', content: response.data.reply }]);
        } catch (err) {
            toast.error('Failed to send message');
        } finally {
            setIsChatting(false);
        }
    };

    const parseAcceptanceCriteria = (criteria: string | string[] | null | undefined): string[] => {
        if (!criteria) return [];
        if (Array.isArray(criteria)) return criteria;
        if (typeof criteria === 'string') {
            try {
                // Try to clean up Postgres array format
                if (criteria.startsWith('{') && criteria.endsWith('}')) {
                    const inner = criteria.slice(1, -1);
                    // Split by comma but respect quotes
                    const matches = inner.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
                    if (matches) return matches.map(m => m.replace(/^"|"$/g, '').trim());
                    return inner.split(',').map(s => s.trim()).filter(Boolean);
                }
                const parsed = JSON.parse(criteria);
                if (Array.isArray(parsed)) return parsed;
            } catch (e) { }
            return [criteria];
        }
        return [];
    };

    const filteredTasks = userStory?.tasks?.filter((task: any) =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const acceptanceCriteriaList = parseAcceptanceCriteria(userStory?.acceptanceCriteria);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-primary-500" />
            </div>
        );
    }

    if (!userStory) return null;

    return (
        <div className="min-h-screen bg-app-bg p-6">
            <LoadingOverlay 
                isOpen={generatingTasks || isEnhancing} 
                message={
                    generatingTasks ? "Generating user story tasks with AI engine..." : "Enhancing task details with AI..."
                } 
            />
            {/* Header / Breadcrumb */}
            <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-1.5 hover:bg-white/10 rounded-lg text-text-secondary hover:text-white transition-colors flex items-center gap-1.5 group"
                        title="Back"
                    >
                        <ChevronLeftIcon className="h-5 w-5 group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>Projects</Button>
                    <span>/</span>
                    <button
                        onClick={() => selectedTask ? setSelectedTask(null) : undefined}
                        className={`truncate max-w-xs ${selectedTask ? 'hover:text-white cursor-pointer transition-colors' : 'cursor-default'}`}
                    >
                        {userStory.title}
                    </button>
                    {!selectedTask && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="ml-4 h-8 px-3 text-xs font-bold border-primary/30 text-primary hover:bg-primary/10 rounded-lg"
                            onClick={() => navigate(`/user-stories/${userStory.id}/test-cases`)}
                        >
                            <BeakerIcon className="h-3.5 w-3.5 mr-1" />
                            Test Cases
                        </Button>
                    )}
                    {selectedTask && (
                        <>
                            <span>/</span>
                            <span className="font-semibold text-white">Task {selectedTask.id !== 'new' ? 'Edit' : 'Create'}</span>
                        </>
                    )}
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-1 gap-6 h-[calc(100vh-100px)]">

                {/* Left Pane */}
                {!selectedTask ? (
                    // View Mode: User Story Details
                    <div className="bg-card backdrop-blur-sm rounded-2xl shadow-sm border border-border p-8 overflow-y-auto animate-in fade-in slide-in-from-left-4 duration-300">
                        <div className="flex items-center justify-between gap-4 mb-6">
                            <h1 className="text-xl font-bold text-white flex-1">{userStory.title}</h1>
                            {userStory.integrationLinks && userStory.integrationLinks.length > 0 && (
                                <div className="flex gap-2 flex-wrap justify-end">
                                    {userStory.integrationLinks.map(link => (
                                        <a 
                                            key={link.id} 
                                            href={link.externalUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-info/10 text-info text-[10px] font-bold border border-info/30 hover:bg-info/20 transition-all uppercase tracking-tight"
                                        >
                                            <LinkIcon className="h-3 w-3" />
                                            {link.provider}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="prose prose-sm max-w-none text-text-secondary space-y-6">
                            <div>
                                <div 
                                    className="leading-relaxed text-base prose prose-sm max-w-none prose-invert"
                                    dangerouslySetInnerHTML={{ __html: userStory.description || '' }}
                                />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-white uppercase tracking-wide mb-3">Acceptance Logic Gates:</h3>
                                <ul className="space-y-4">
                                    {acceptanceCriteriaList.length > 0 ? (
                                        acceptanceCriteriaList.map((criterion, idx) => (
                                            <li key={idx} className="flex items-start gap-3 text-text-secondary">
                                                <span className="font-medium text-text-secondary text-sm mt-0.5 select-none">{idx + 1}.</span>
                                                <span className="leading-relaxed">{criterion}</span>
                                            </li>
                                        ))
                                    ) : <li className="text-text-secondary italic">No acceptance criteria available</li>}
                                </ul>
                            </div>
                        </div>
                    </div>
                ) : (
                    // Edit Mode: Task Edit Form
                    <div className="bg-card rounded-2xl shadow-lg border border-border flex flex-col overflow-hidden animate-in fade-in slide-in-from-left-4 duration-300 text-white">
                        <div className="p-6 border-b border-border flex justify-between items-center bg-app-bg/50">
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setSelectedTask(null)}
                                    className="p-1.5 hover:bg-white/10 rounded-lg text-text-secondary hover:text-white transition-colors flex items-center gap-1.5 group"
                                    title="Back to Task List"
                                >
                                    <ChevronLeftIcon className="h-5 w-5 group-hover:-translate-x-0.5 transition-transform" />
                                </button>
                                <h2 className="text-lg font-bold text-white">
                                    {selectedTask.id === 'new' ? 'Create Task' : 'Edit Task'}
                                </h2>
                            </div>
                            {selectedTask.id !== 'new' && (
                                <button
                                    onClick={() => setShowDeleteTaskModal(true)}
                                    className="p-2 text-text-secondary hover:text-error hover:bg-error/10 rounded-lg transition-all"
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                        <div className="flex-1 overflow-y-auto p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-text-secondary uppercase tracking-wider">Task Name <span className="text-error">*</span></label>
                                <input
                                    type="text"
                                    value={editTaskTitle}
                                    onChange={(e) => setEditTaskTitle(e.target.value)}
                                    className="w-full bg-app-bg border border-border rounded-lg p-3 text-sm focus:ring-2 focus:ring-primary focus:border-primary transition-all font-medium text-white"
                                    placeholder="Enter task name..."
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-text-secondary uppercase tracking-wider">Task Description <span className="text-error">*</span></label>
                                <div className="bg-app-bg rounded-lg border border-border overflow-hidden focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition-all text-white">
                                    <ReactQuill
                                        theme="snow"
                                        value={editTaskDescription}
                                        onChange={setEditTaskDescription}
                                        className="h-96"
                                        modules={{
                                            toolbar: [
                                                [{ 'header': [1, 2, false] }],
                                                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                ['link'],
                                                ['clean']
                                            ]
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t border-border flex items-center justify-between bg-card">
                            <Button
                                variant="outline"
                                onClick={handleEnhanceTask}
                                disabled={isEnhancing || !editTaskDescription}
                                className="text-primary border-primary/30 hover:bg-primary/10"
                            >
                                <SparklesIcon className={`h-4 w-4 mr-2 ${isEnhancing ? 'animate-spin' : ''}`} />
                                Enhance with AI
                            </Button>
                            <div className="flex gap-3">
                                <Button variant="ghost" onClick={() => setSelectedTask(null)}>Cancel</Button>
                                <Button
                                    onClick={handleSaveTask}
                                    disabled={isSavingTask}
                                    className="bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-500/20"
                                >
                                    {isSavingTask ? 'Saving...' : 'Update'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Right Pane */}
                {!selectedTask ? (
                    // View Mode: Task List
                    <div className="bg-card backdrop-blur-sm rounded-2xl shadow-sm border border-border flex flex-col h-full overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="p-6 border-b border-border flex justify-between items-center bg-card sticky top-0 z-10">
                            <div className="flex items-center gap-2">
                                <h2 className="text-lg font-bold text-white">Tasks</h2>
                                <span className="bg-app-bg text-text-secondary px-2 py-0.5 rounded-full text-xs font-medium border border-border">
                                    {userStory.tasks?.length || 0}
                                </span>
                            </div>
                            <div className="flex gap-2.5">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={generatingTasks}
                                    onClick={handleOpenGenerateModal}
                                    className="text-primary border-primary/30 bg-primary/10 hover:bg-primary/20"
                                >
                                    {generatingTasks ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin mr-2" /> : <SparklesIcon className="h-3.5 w-3.5 mr-2" />}
                                    {generatingTasks ? 'Generating...' : (!userStory.tasks || userStory.tasks.length === 0 ? 'Generate Tasks' : 'Regenerate Tasks')}
                                </Button>
                                <Button size="sm" onClick={handleAddNewTask} className="bg-orange-600 hover:bg-orange-700 text-white shadow-md">
                                    <PlusIcon className="h-3.5 w-3.5 mr-1" />
                                    Add New
                                </Button>
                            </div>
                        </div>

                        <div className="p-4 border-b border-border bg-app-bg/30">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 bg-app-bg border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all text-white"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-app-bg/20">
                            {filteredTasks.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                                    <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-48 w-48 object-contain mb-6 opacity-80" />
                                    <h3 className="text-white font-bold mb-1">No Tasks Available</h3>
                                    <p className="text-text-secondary text-sm mb-6">Generate tasks using AI or add one manually</p>
                                    <div className="flex gap-3">
                                        <Button
                                            onClick={handleOpenGenerateModal}
                                            disabled={generatingTasks}
                                            className="bg-primary/20 hover:bg-primary/30 text-primary shadow-sm border border-primary/30"
                                        >
                                            {generatingTasks ? <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" /> : <SparklesIcon className="h-4 w-4 mr-2" />}
                                            Auto Generate Tasks
                                        </Button>
                                        <Button
                                            onClick={handleAddNewTask}
                                            className="bg-orange-600 hover:bg-orange-700 text-white shadow-md"
                                        >
                                            <PlusIcon className="h-4 w-4 mr-2" />
                                            Add Manually
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                filteredTasks.map((task: any) => (
                                    <div
                                        key={task.id}
                                        onClick={() => handleTaskClick(task)}
                                        className="bg-app-bg p-4 rounded-xl border border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all group cursor-pointer relative"
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className="font-semibold text-white text-sm group-hover:text-primary">{task.title}</h4>
                                            <ClockIcon className="h-4 w-4 text-text-secondary group-hover:text-primary" />
                                        </div>
                                        <div dangerouslySetInnerHTML={{ __html: task.description || '' }} className="text-xs text-text-secondary line-clamp-2" />
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    // Edit Mode: Chat Interface (Temporarily Commented Out)
                    <div className="hidden">
                        {/* 
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col h-full overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="p-4 border-b border-gray-50 flex items-center justify-center bg-orange-50/30">
                                <span className="flex items-center gap-2 text-sm font-bold text-orange-600">
                                    <SparklesIcon className="h-4 w-4" />
                                    Talk to DocSprintAI
                                    <span className="bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded text-[10px] font-bold border border-orange-200 uppercase">Beta</span>
                                </span>
                            </div>
                            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                {chatHistory.map((msg, idx) => (
                                    <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                        <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'ai' ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-600'}`}>
                                            {msg.role === 'ai' ? <SparklesIcon className="h-4 w-4" /> : <div className="text-xs font-bold">U</div>}
                                        </div>
                                        <div className={`p-4 rounded-2xl text-sm max-w-[85%] ${msg.role === 'ai' ? 'bg-orange-50 text-gray-800 rounded-tl-none' : 'bg-gray-100 text-gray-800 rounded-tr-none'}`}>
                                            {msg.content}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-4 border-t border-gray-50">
                                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 focus-within:ring-2 focus-within:ring-orange-500 transition-all">
                                    <textarea
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        placeholder="Chat to add or modify your requirement"
                                        rows={2}
                                        className="w-full bg-transparent text-sm outline-none resize-none px-1"
                                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(); } }}
                                    />
                                    <div className="flex justify-between items-center mt-2 px-1">
                                        <LinkIcon className="h-5 w-5 text-gray-400 cursor-pointer hover:text-gray-600" />
                                        <Button size="sm" onClick={handleChat} disabled={isChatting} className="bg-orange-600 hover:bg-orange-700 px-6 rounded-lg">
                                            <PaperAirplaneIcon className="h-4 w-4 text-white" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        */}
                    </div>
                )}
            </div>

            {/* Modals */}
            <ConfirmationModal
                isOpen={showDeleteTaskModal}
                onClose={() => setShowDeleteTaskModal(false)}
                onConfirm={handleDeleteTask}
                isLoading={isDeletingTask}
                title="Delete Task"
                message="Are you sure you want to delete this task? This action cannot be undone."
                confirmLabel="Delete"
                variant="danger"
            />

            {showTaskGenerationModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    {/* ... Reusing existing generation modal structure if needed, or keeping simplified ... */}
                    <div className="bg-card rounded-lg shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 border border-border">
                        <div className="p-6 border-b border-border">
                            <h2 className="text-xl font-bold text-white">{userStory.tasks && userStory.tasks.length > 0 ? 'Regenerate User Story Tasks' : 'Generate User Story Tasks'}</h2>
                            <p className="text-sm text-text-secondary mt-1">{userStory.tasks && userStory.tasks.length > 0 ? 'Provide feedback on existing tasks or provide new context for regeneration (Optional)' : 'Include additional context to generate relevant user story tasks (Optional)'}</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-2">{userStory.tasks && userStory.tasks.length > 0 ? 'Regeneration Thoughts / Feedback (Optional)' : 'Additional Context (Optional)'}</label>
                                <textarea
                                    className="w-full bg-app-bg border border-border text-white rounded-md px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent min-h-[100px] resize-none"
                                    placeholder={userStory.tasks && userStory.tasks.length > 0 ? "Provide your thoughts, vision, or feedback on previous output..." : "Add additional context for the user story tasks..."}
                                    value={extraContext}
                                    onChange={(e) => setExtraContext(e.target.value)}
                                />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-sm font-medium text-text-secondary">Number of Tasks</label>
                                    <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs font-bold border border-primary/30">{taskCount}</span>
                                </div>
                                <Slider
                                    value={taskCount}
                                    min={1}
                                    max={15}
                                    step={1}
                                    onChange={setTaskCount}
                                />
                                <div className="flex justify-between text-[10px] font-medium text-text-secondary mt-1 px-1">
                                    <span>1 Task</span>
                                    <span>15 Tasks</span>
                                </div>
                                {!isPro && (
                                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-warning uppercase tracking-tighter mt-2 bg-warning/10 w-fit px-2 py-1 rounded border border-warning/20">
                                        <SparklesIcon className="h-3 w-3" />
                                        Starter plan: Max 15 tasks. Only on your active project.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-6 bg-app-bg/50 border-t border-border flex justify-between items-center">
                            <Button variant="outline" onClick={() => setShowTaskGenerationModal(false)} className="text-white border-border hover:bg-white/5">Cancel</Button>
                            <Button className="bg-primary hover:bg-orange-600 text-white" onClick={handleGenerateTasks}>Generate</Button>
                        </div>
                    </div>
                </div>
            )}

            {showProgressModal && (
                <div className="fixed inset-0 bg-app-bg/80 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-card rounded-xl shadow-2xl border border-border p-8 max-w-lg w-full animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-white">Generating Tasks...</h3>
                            <span className="flex items-center gap-2 text-sm text-primary bg-primary/10 px-3 py-1 rounded-full font-medium border border-primary/20">
                                <span className="h-2 w-2 rounded-full bg-primary animate-pulse"></span>
                                Processing
                            </span>
                        </div>
                        <div className="space-y-4">
                            {progressSteps.map((step, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-app-bg/50">
                                    {step.status === 'completed' ? <CheckCircleIcon className="h-5 w-5 text-green-500" /> :
                                        step.status === 'loading' ? <ArrowPathIcon className="h-5 w-5 text-info animate-spin" /> :
                                            <div className="h-5 w-5 rounded-full border-2 border-border"></div>}
                                    <span className={`text-sm ${step.status === 'loading' ? 'text-primary font-medium' : step.status === 'completed' ? 'text-green-500' : 'text-text-secondary'}`}>
                                        {step.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
